/**
 * Shared Navigation Items
 *
 * Single source of truth for sidebar nav structure used by both
 * desktop Sidebar and MobileSidebar. Extracted to avoid divergence.
 */

import {
  LayoutDashboard,
  Users,
  Calendar,
  CalendarCheck,
  CalendarDays,
  DollarSign,
  Clock,
  FileText,
  ListChecks,
  Settings,
  Building2,
  ChevronDown,
  ChevronUp,
  Briefcase,
  UserPlus,
  Target,
  ClipboardList,
  Kanban,
  BarChart3,
  FileBarChart,
  FileSearch,
  Zap,
  Bell,
  Mail,
  GitBranch,
  Package,
  CalendarClock,
  CalendarRange,
  Shield,
  CreditCard,
  Timer,
  ArrowRightLeft,
  Wallet,
  FileUser,
  User,
  Server,
  Heart,
  ClipboardCheck,
  Receipt,
  AlertTriangle,
  FilePenLine,
  Network,
  MapPin,
  Award,
  Archive,
  Store,
  ShoppingCart,
  FileSignature,
  Headset,
  GitMerge,
  ScrollText,
  TrendingUp,
  Swords,
  Navigation,
  History,
  Banknote,
  ShieldCheck,
  FileUp,
  type LucideIcon,
} from 'lucide-react';
import {
  OVERTIME_PAGE_ACCESS_PERMISSIONS,
  REGULARIZATION_PAGE_ACCESS_PERMISSIONS,
} from '@/features/attendance/permission-contracts';

export { ChevronDown, ChevronUp };

export interface NavItem {
  title: string;
  href: string;
  icon: LucideIcon;
  module?: string;
  permission?: string;
  anyPermission?: string[];
  requiredRole?: string | string[];
  badge?: string;
  children?: NavItem[];
  sectionLabel?: string;
  activePaths?: string[];
}

export const navItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'CRM',
    href: '/crm',
    icon: Briefcase,
    module: 'crm',
    children: [
      { title: 'Overview', href: '/crm', icon: LayoutDashboard },
      {
        title: 'Pipeline',
        href: '/crm/pipeline',
        icon: Kanban,
        anyPermission: [
          'crm:pipeline:read:self',
          'crm:pipeline:read:team',
          'crm:pipeline:read:department',
          'crm:pipeline:read:tenant',
        ],
      },
      {
        title: 'Leads',
        href: '/crm/leads',
        icon: UserPlus,
        anyPermission: [
          'crm:lead:read:self',
          'crm:lead:read:team',
          'crm:lead:read:department',
          'crm:lead:read:tenant',
        ],
      },
      {
        title: 'Contacts',
        href: '/crm/contacts',
        icon: Users,
        anyPermission: [
          'crm:contact:read:self',
          'crm:contact:read:team',
          'crm:contact:read:department',
          'crm:contact:read:tenant',
        ],
      },
      {
        title: 'Accounts',
        href: '/crm/accounts',
        icon: Building2,
        anyPermission: [
          'crm:account:read:self',
          'crm:account:read:team',
          'crm:account:read:department',
          'crm:account:read:tenant',
        ],
      },
      {
        title: 'Deals',
        href: '/crm/deals',
        icon: Target,
        anyPermission: [
          'crm:deal:read:self',
          'crm:deal:read:team',
          'crm:deal:read:department',
          'crm:deal:read:tenant',
        ],
      },
      {
        title: 'Tasks',
        href: '/crm/tasks',
        icon: ClipboardList,
        anyPermission: [
          'crm:task:read:self',
          'crm:task:read:team',
          'crm:task:read:department',
          'crm:task:read:tenant',
        ],
      },
      {
        title: 'Products',
        href: '/crm/products',
        icon: Package,
        anyPermission: [
          'crm:product:read:self',
          'crm:product:read:team',
          'crm:product:read:department',
          'crm:product:read:tenant',
        ],
      },
      {
        title: 'Vendors',
        href: '/crm/vendors',
        icon: Store,
        anyPermission: [
          'crm:vendor:read:self',
          'crm:vendor:read:team',
          'crm:vendor:read:department',
          'crm:vendor:read:tenant',
        ],
      },
      {
        title: 'Purchase Orders',
        href: '/crm/purchase-orders',
        icon: ShoppingCart,
        permission: 'crm:purchase-order:read:tenant',
      },
      {
        title: 'Quotes',
        href: '/crm/quotes',
        icon: FileSignature,
        permission: 'crm:quote:read:tenant',
      },
      {
        title: 'Sales Orders',
        href: '/crm/sales-orders',
        icon: ClipboardCheck,
        anyPermission: [
          'crm:sales-order:read:self',
          'crm:sales-order:read:team',
          'crm:sales-order:read:department',
          'crm:sales-order:read:tenant',
        ],
      },
      {
        title: 'Invoices',
        href: '/crm/invoices',
        icon: Receipt,
        anyPermission: [
          'crm:invoice:read:self',
          'crm:invoice:read:team',
          'crm:invoice:read:department',
          'crm:invoice:read:tenant',
        ],
      },
      {
        title: 'Payments',
        href: '/crm/payments',
        icon: Banknote,
        anyPermission: [
          'crm:invoice:read:self',
          'crm:invoice:read:team',
          'crm:invoice:read:department',
          'crm:invoice:read:tenant',
        ],
      },
      {
        title: 'Credit Notes',
        href: '/crm/credit-notes',
        icon: FileText,
        anyPermission: [
          'crm:invoice:read:self',
          'crm:invoice:read:team',
          'crm:invoice:read:department',
          'crm:invoice:read:tenant',
        ],
      },
      {
        title: 'Cases',
        href: '/crm/cases',
        icon: Headset,
        anyPermission: [
          'crm:case:read:self',
          'crm:case:read:team',
          'crm:case:read:department',
          'crm:case:read:tenant',
        ],
      },
      {
        title: 'Contracts',
        href: '/crm/contracts',
        icon: ScrollText,
        anyPermission: [
          'crm:contract:read:self',
          'crm:contract:read:team',
          'crm:contract:read:department',
          'crm:contract:read:tenant',
        ],
      },
      {
        title: 'Activities',
        href: '/crm/activities',
        icon: CalendarCheck,
        permission: 'crm:activity:read:tenant',
      },
      {
        title: 'Site Visits',
        href: '/crm/site-visits',
        icon: Navigation,
        anyPermission: [
          'crm:site-visit:read:self',
          'crm:site-visit:read:team',
          'crm:site-visit:read:department',
          'crm:site-visit:read:tenant',
        ],
      },
      {
        title: 'Calendar',
        href: '/crm/calendar',
        icon: CalendarDays,
        permission: 'crm:activity:read:tenant',
      },
      {
        title: 'Campaigns',
        href: '/crm/campaigns',
        icon: Mail,
        anyPermission: [
          'crm:campaign:read:self',
          'crm:campaign:read:team',
          'crm:campaign:read:department',
          'crm:campaign:read:tenant',
        ],
      },
      {
        title: 'Analytics',
        href: '/crm/analytics',
        icon: BarChart3,
        anyPermission: [
          'crm:analytics:read:self',
          'crm:analytics:read:team',
          'crm:analytics:read:department',
          'crm:analytics:read:tenant',
        ],
      },
      {
        title: 'Reports',
        href: '/crm/reports',
        icon: FileBarChart,
        anyPermission: [
          'crm:reports:read:self',
          'crm:reports:read:team',
          'crm:reports:read:department',
          'crm:reports:read:tenant',
        ],
      },
      {
        title: 'Automation',
        href: '/crm/automation',
        icon: Zap,
        permission: 'crm:workflow:read:tenant',
      },
      {
        title: 'Journeys',
        href: '/crm/journeys',
        icon: GitBranch,
        activePaths: ['/crm/journeys'],
        permission: 'crm:workflow:read:tenant',
      },
      {
        title: 'Email Templates',
        href: '/crm/email-templates',
        icon: Mail,
        permission: 'crm:email-template:read:tenant',
      },
      {
        title: 'Archive',
        href: '/crm/archive',
        icon: Archive,
        activePaths: ['/crm/archive', '/crm/settings/archive'],
        permission: 'crm:archive:read:tenant',
      },
      {
        title: 'Duplicates',
        href: '/crm/duplicates',
        icon: GitMerge,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Forecasts',
        href: '/crm/forecasts',
        icon: TrendingUp,
        anyPermission: [
          'crm:forecast:read:self',
          'crm:forecast:read:team',
          'crm:forecast:read:department',
          'crm:forecast:read:tenant',
        ],
      },
      {
        title: 'Territories',
        href: '/crm/territories',
        icon: MapPin,
        permission: 'crm:territory:read:tenant',
      },
      {
        title: 'Competitors',
        href: '/crm/competitors',
        icon: Swords,
        permission: 'crm:competitor:read:tenant',
      },
      {
        title: 'Settings',
        href: '/crm/settings/general',
        icon: Settings,
        activePaths: [
          '/crm/settings/general',
          '/crm/settings/pipelines',
          '/crm/settings/archive',
          '/crm/settings/lead-scoring',
          '/crm/settings/webhooks',
          '/crm/settings/sla',
          '/crm/settings/custom-fields',
        ],
        permission: 'crm:setting:update:tenant',
      },
    ],
  },
  {
    title: 'HRMS',
    href: '/employees',
    icon: Users,
    module: 'hrms',
    anyPermission: [
      'hrms:employee:read:tenant',
      'hrms:employee:read:self',
      'hrms:directory:read:tenant',
      'hrms:legal-entity:read:tenant',
      'hrms:department:read:tenant',
      'hrms:team:read:tenant',
    ],
    children: [
      {
        title: 'Companies',
        href: '/companies',
        icon: Building2,
        permission: 'hrms:legal-entity:read:tenant',
      },
      {
        title: 'All Employees',
        href: '/employees',
        icon: Users,
        permission: 'hrms:employee:read:tenant',
      },
      {
        title: 'My Profile',
        href: '/employees/me',
        icon: Users,
        permission: 'hrms:employee:read:self',
      },
      {
        title: 'My Team',
        href: '/employees/my-team',
        icon: Users,
        anyPermission: ['hrms:employee:read:team', 'hrms:employee:read:department'],
        requiredRole: ['people_manager', 'team_lead', 'department_head'],
      },
      {
        title: 'Departments',
        href: '/departments',
        icon: GitBranch,
        permission: 'hrms:department:read:tenant',
      },
      {
        title: 'Teams',
        href: '/teams',
        icon: Users,
        permission: 'hrms:team:read:tenant',
      },
      {
        title: 'Designations',
        href: '/designations',
        icon: Briefcase,
        permission: 'hrms:designation:read:tenant',
      },
      {
        title: 'Positions',
        href: '/positions',
        icon: Network,
        anyPermission: [
          'hrms:position:create:tenant',
          'hrms:position:update:tenant',
          'hrms:position:assign:tenant',
        ],
      },
      {
        title: 'Locations',
        href: '/locations',
        icon: MapPin,
        anyPermission: ['hrms:location:create:tenant', 'hrms:location:update:tenant'],
      },
      { title: 'Assets', href: '/assets', icon: Package, permission: 'hrms:asset:read:tenant' },
      {
        title: 'Recruitment',
        href: '/recruitment',
        icon: FileUser,
        permission: 'hrms:recruitment:read:tenant',
      },
      {
        title: 'Onboarding',
        href: '/onboarding',
        icon: UserPlus,
        permission: 'hrms:onboarding:read:tenant',
      },
      {
        title: 'Program Templates',
        href: '/onboarding/program-templates',
        icon: ListChecks,
        permission: 'hrms:onboarding:template:read:tenant',
      },
      {
        title: 'Document Templates',
        href: '/onboarding/document-templates',
        icon: FileText,
        permission: 'hrms:onboarding:template:read:tenant',
      },
      {
        title: 'Learning',
        href: '/learning',
        icon: FileText,
        permission: 'hrms:learning:read:tenant',
      },
    ],
  },
  {
    title: 'Holidays',
    href: '/holidays',
    icon: CalendarDays,
    module: 'hrms',
    anyPermission: [
      'hrms:holiday:read:tenant',
      'hrms:holiday:read:self',
      'hrms:holiday:approve:team',
      'hrms:holiday:manage:tenant',
    ],
    children: [
      {
        title: 'Calendar',
        href: '/holidays',
        icon: CalendarDays,
        anyPermission: ['hrms:holiday:read:tenant'],
      },
      {
        title: 'My Holidays',
        href: '/my-holidays',
        icon: Calendar,
        permission: 'hrms:holiday:read:self',
      },
      {
        title: 'Floating Approvals',
        href: '/holidays/floating-approvals',
        icon: CalendarCheck,
        permission: 'hrms:holiday:approve:team',
      },
      {
        title: 'Policy',
        href: '/holidays/policy',
        icon: Settings,
        permission: 'hrms:holiday:manage:tenant',
      },
    ],
  },
  {
    title: 'Leave',
    href: '/leaves',
    icon: Calendar,
    module: 'hrms',
    anyPermission: [
      'hrms:leave-request:read:tenant',
      'hrms:leave-request:read:self',
      'hrms:leave-balance:read:self',
    ],
    children: [
      {
        title: 'Requests',
        href: '/leaves/requests',
        icon: CalendarCheck,
        anyPermission: ['hrms:leave-request:read:tenant', 'hrms:leave-request:read:self'],
      },
      {
        title: 'Calendar',
        href: '/leaves/calendar',
        icon: CalendarDays,
        anyPermission: ['hrms:leave-request:read:tenant', 'hrms:leave-request:read:self'],
      },
      {
        title: 'Balance',
        href: '/leaves/balance',
        icon: Wallet,
        permission: 'hrms:leave-balance:read:self',
      },
      {
        title: 'Transactions',
        href: '/leaves/transactions',
        icon: History,
        permission: 'hrms:leave-balance:read:self',
      },
      {
        title: 'Encashment',
        href: '/leaves/encashment',
        icon: Banknote,
        permission: 'hrms:leave-balance:read:self',
      },
      {
        title: 'Delegations',
        href: '/leaves/delegations',
        icon: Users,
        permission: 'hrms:approval-delegation:manage:self',
      },
      {
        title: 'Admin',
        href: '/leaves/admin',
        icon: ShieldCheck,
        permission: 'hrms:leave-admin:manage:tenant',
      },
    ],
  },
  {
    title: 'Attendance',
    href: '/attendance',
    icon: Clock,
    module: 'attendance',
    anyPermission: [
      'attendance:record:read:tenant',
      'attendance:record:read:self',
      'attendance:regularization:read:tenant',
      'attendance:regularization:approve:team',
      'attendance:regularization:approve:department',
      'attendance:regularization:approve:tenant',
      'attendance:bulk:read:tenant',
      'attendance:bulk:approve:tenant',
    ],
    children: [
      {
        title: 'Dashboard',
        href: '/attendance/dashboard',
        icon: LayoutDashboard,
        anyPermission: ['attendance:record:read:tenant', 'attendance:record:read:self'],
      },
      {
        title: 'Timesheet',
        href: '/attendance/timesheet',
        icon: Timer,
        permission: 'attendance:timesheet:read:self',
      },
      {
        title: 'Approvals',
        href: '/attendance/timesheet/approvals',
        icon: ClipboardCheck,
        anyPermission: [
          'attendance:timesheet:approve:team',
          'attendance:timesheet:approve:department',
          'attendance:timesheet:approve:tenant',
        ],
      },
      {
        title: 'Team Attendance',
        href: '/attendance/team-attendance',
        icon: Users,
        anyPermission: [
          'attendance:record:read:team',
          'attendance:record:read:department',
          'attendance:record:read:tenant',
        ],
      },
      {
        title: 'Regularization',
        href: '/attendance/regularization',
        icon: FilePenLine,
        anyPermission: REGULARIZATION_PAGE_ACCESS_PERMISSIONS,
      },
      {
        title: 'Violations',
        href: '/attendance/violations',
        icon: AlertTriangle,
        anyPermission: [
          'attendance:violation:read:tenant',
          'attendance:violation:read:department',
          'attendance:violation:read:team',
          'attendance:violation:read:self',
        ],
      },
      {
        title: 'Overtime',
        href: '/attendance/overtime',
        icon: Timer,
        anyPermission: OVERTIME_PAGE_ACCESS_PERMISSIONS,
      },
      {
        title: 'Device Events',
        href: '/attendance/device-events',
        icon: Server,
        permission: 'attendance:time-event:read:tenant',
      },
      {
        title: 'Imports',
        href: '/attendance/imports',
        icon: FileUp,
        anyPermission: ['attendance:bulk:read:tenant', 'attendance:bulk:approve:tenant'],
      },
      {
        title: 'Analytics',
        href: '/attendance/analytics',
        icon: BarChart3,
        permission: 'attendance:analytics:read:tenant',
      },
      {
        title: 'Policies',
        href: '/attendance/policies',
        icon: Settings,
        permission: 'attendance:policy:read:tenant',
      },
    ],
  },
  {
    title: 'Shifts',
    href: '/shifts',
    icon: CalendarClock,
    module: 'attendance',
    anyPermission: ['attendance:shift:read:tenant', 'attendance:shift_schedule:read:self'],
    children: [
      {
        title: 'Management',
        href: '/shifts',
        icon: CalendarClock,
        permission: 'attendance:shift:read:tenant',
      },
      {
        title: 'Roster',
        href: '/shifts/roster',
        icon: CalendarRange,
        activePaths: ['/shifts/roster', '/shifts/day-roster'],
        anyPermission: ['attendance:shift:read:tenant', 'attendance:shift_schedule:read:self'],
      },
      {
        title: 'Swap',
        href: '/shifts/swap',
        icon: ArrowRightLeft,
        anyPermission: ['attendance:shift-swap:read:tenant', 'attendance:shift-swap:read:self'],
      },
      {
        title: 'Change Requests',
        href: '/shifts/change-requests',
        icon: FilePenLine,
        anyPermission: [
          'attendance:shift_change:read:self',
          'attendance:shift_change:read:team',
          'attendance:shift_change:read:tenant',
        ],
      },
    ],
  },
  {
    title: 'Skills',
    href: '/skills',
    icon: Award,
    module: 'hrms',
    permission: 'hrms:skill:read:tenant',
  },
  {
    title: 'Payroll',
    href: '/hrms/payroll',
    icon: DollarSign,
    module: 'hrms',
    anyPermission: [
      'hrms:payroll-run:read:tenant',
      'hrms:payslip:read:self',
      'hrms:payroll-tax:read:self',
      'hrms:salary:read:tenant',
    ],
    children: [
      {
        title: 'Payroll Runs',
        href: '/hrms/payroll',
        icon: Banknote,
        permission: 'hrms:payroll-run:read:tenant',
      },
      {
        title: 'My Payslips',
        href: '/hrms/payroll/my-payslips',
        icon: Receipt,
        permission: 'hrms:payslip:read:self',
      },
      {
        title: 'Salary Structures',
        href: '/hrms/payroll/salaries',
        icon: Wallet,
        permission: 'hrms:salary:read:tenant',
      },
      {
        title: 'Salary Components',
        href: '/hrms/payroll/components',
        icon: CreditCard,
        anyPermission: [
          'hrms:salary-component:read:tenant',
          'hrms:salary-component:create:tenant',
          'hrms:salary-component:update:tenant',
        ],
      },
      {
        title: 'Statutory Config',
        href: '/hrms/payroll/config',
        icon: ShieldCheck,
        permission: 'hrms:statutory-config:read:tenant',
      },
      {
        title: 'Tax Declarations',
        href: '/hrms/payroll/tax-declarations',
        icon: FileText,
        anyPermission: ['hrms:payroll-tax:read:self', 'hrms:payroll-tax:read:tenant'],
      },
    ],
  },
  {
    title: 'Benefits',
    href: '/benefits',
    icon: Heart,
    module: 'hrms',
    anyPermission: [
      'hrms:benefits:plan:create:tenant',
      'hrms:benefits:plan:update:tenant',
      'hrms:benefits:enrollment:read:tenant',
      'hrms:benefits:claim:read:tenant',
      'hrms:benefits:statistics:read:tenant',
      'hrms:benefits:enrollment:read:self',
      'hrms:benefits:claim:read:self',
    ],
    children: [
      {
        title: 'My Benefits',
        href: '/benefits/my',
        icon: Heart,
        permission: 'hrms:benefits:enrollment:read:self',
      },
      {
        title: 'Overview',
        href: '/benefits',
        icon: Heart,
        permission: 'hrms:benefits:statistics:read:tenant',
      },
      {
        title: 'Plans',
        href: '/benefits/plans',
        icon: ClipboardCheck,
        anyPermission: ['hrms:benefits:plan:create:tenant', 'hrms:benefits:plan:update:tenant'],
      },
      {
        title: 'Enrollments',
        href: '/benefits/enrollments',
        icon: Users,
        permission: 'hrms:benefits:enrollment:read:tenant',
      },
      {
        title: 'Claims',
        href: '/benefits/claims',
        icon: Receipt,
        permission: 'hrms:benefits:claim:read:tenant',
      },
    ],
  },
  {
    title: 'Reports',
    href: '/reports',
    icon: FileText,
    module: 'hrms',
    permission: 'hrms:reports:read:tenant',
  },
  {
    title: 'Notifications',
    href: '/notifications',
    icon: Bell,
    permission: 'gateway:notifications:read:self',
  },
  {
    title: 'Settings',
    href: '/settings',
    icon: Settings,
    children: [
      {
        title: 'My Account',
        href: '/settings/profile',
        icon: User,
        activePaths: ['/settings/profile', '/settings/preferences', '/settings/notifications'],
      },
      {
        title: 'Organization',
        href: '/settings/organization',
        icon: Building2,
        anyPermission: ['hrms:department:read:tenant', 'hrms:team:read:tenant'],
        activePaths: ['/settings/organization/'],
      },
      {
        title: 'Users & Security',
        href: '/settings/users',
        icon: Shield,
        anyPermission: [
          'tenant:users:read:tenant',
          'abac:role:read:tenant',
          'auth:config:read:tenant',
        ],
        activePaths: ['/settings/users', '/settings/roles', '/settings/auth'],
      },
      {
        title: 'Audit Logs',
        href: '/settings/audit-logs',
        icon: FileSearch,
        anyPermission: ['auth:audit:read:tenant', 'hrms:audit:read:tenant'],
        activePaths: ['/settings/audit-logs'],
      },
      {
        title: 'Billing',
        href: '/settings/billing',
        icon: CreditCard,
        permission: 'tenant:plans:read:tenant',
        activePaths: ['/settings/billing'],
      },
    ],
  },
];

export const superadminNavItems: NavItem[] = [
  {
    title: 'Tenants',
    href: '/settings/system/tenants',
    icon: Building2,
  },
  {
    title: 'Notifications',
    href: '/notifications',
    icon: Bell,
    requiredRole: 'superadmin',
  },
  {
    title: 'Settings',
    href: '/settings',
    icon: Settings,
    children: [
      {
        title: 'My Account',
        href: '/settings/profile',
        icon: User,
        activePaths: ['/settings/profile', '/settings/preferences', '/settings/notifications'],
      },
      {
        title: 'Users & Security',
        href: '/settings/users',
        icon: Shield,
        activePaths: [
          '/settings/users',
          '/settings/roles',
          '/settings/auth',
          '/settings/audit-logs',
        ],
      },
      {
        title: 'System',
        href: '/settings/system/config',
        icon: Server,
        activePaths: ['/settings/system/'],
      },
    ],
  },
];

export const adminNavItems: NavItem[] = [];
